import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { BrowseResultItem, RotationOffsets } from '@cartwhl/common/types';
import { DATADOG_ACTIONS } from '@cartwhl/tracking/datadogConstants';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';

import { AnimationMiniPlayer, MiniControls } from 'components/AnimationPlayer';
import useBreakpoint from 'hooks/useBreakpoint';
import {
  browseMotionStore,
  projectStore,
  sceneSettingsStore,
  uiStore,
} from 'stores';
import {
  getAnimationPlayerStoreByID,
  removeAnimationPlayerStoreByID,
} from 'stores/animationPlayerStoreFactory';
import getCharacterDataFromProject from 'utils/getCharacterDataFromProject';

import { BROWSE_DESKTOP_MIN_WIDTH } from '../constants';
import BrowseRecordFootContacts from './BrowseRecordFootContacts';

type Props = {
  browseItem: BrowseResultItem;
  onAddMotion: () => void;
};

export default function ReviewBrowseMotion({ browseItem, onAddMotion }: Props) {
  const browseAnimationPlayer = getAnimationPlayerStoreByID(browseItem.jobID);
  const { reviewMode } = useSnapshot(browseMotionStore.state);
  const { isMobile } = useBreakpoint(BROWSE_DESKTOP_MIN_WIDTH);
  const addText = isMobile ? 'Add' : 'Add motion';
  const useText = isMobile ? 'Use new' : 'Use as new motion';

  const { currentProject } = useSnapshot(projectStore.state);
  const characterData = getCharacterDataFromProject(currentProject?.data);

  // when the component unmounts lets call removeAnimationPlayerStoreByID
  useEffect(() => {
    return () => {
      removeAnimationPlayerStoreByID(browseItem.jobID);
    };
  }, []);

  const [animationPath, setAnimationPath] = useState<string | undefined>();
  const {
    cameraSettings: { cameraFollow, serializedCamera },
    characterSettings,
    playbackSettings: { frameRate, playbackSpeed, frameStepSize },
  } = useSnapshot(sceneSettingsStore.state);

  const [prevCameraFollow] = useState<boolean>(cameraFollow);

  useEffect(() => {
    return () => {
      sceneSettingsStore.set(
        'cameraSettings',
        'cameraFollow',
        prevCameraFollow,
      );
    };
  }, []);

  useEffect(() => {
    const fetchBvh = async () => {
      const bvhUrl = await browseMotionStore.fetchBvhFromJob(browseItem.jobID);
      setAnimationPath(bvhUrl);
    };
    fetchBvh();
  }, [browseItem.jobID]);

  // Go back to view the list of all motions after submitting foot positions
  const handleFeedbackSubmitted = () => {
    onAddMotion();
  };

  return (
    <div className="relative flex h-full flex-col px-4 py-3">
      <div
        className={`${
          reviewMode ? 'min-h-[400px] flex-1' : 'min-h-[315px] flex-1'
        } rounded-lg ${reviewMode ? 'rounded-lg' : ''}`}
      >
        {animationPath ? (
          <AnimationMiniPlayer
            meshPath={characterData?.meshPath}
            characterID={characterData?.characterID}
            rotationOffsets={characterData?.rotationOffsets as RotationOffsets}
            playerName="browse"
            animationPath={animationPath}
            animationPlayerStore={browseAnimationPlayer}
            cameraFollow={cameraFollow}
            characterSettings={characterSettings}
            defaultCameraPosition="far"
            forceShowSkeleton={false}
            frameRate={frameRate}
            frameStepSize={frameStepSize}
            playbackSpeed={playbackSpeed}
            serializedCamera={serializedCamera}
            showCameraButtons={true}
            showTightGrid={reviewMode}
            miniControls={
              <MiniControls.Root animationPlayerStore={browseAnimationPlayer}>
                <MiniControls.PlayButton />
                <MiniControls.Timeline />
              </MiniControls.Root>
            }
          />
        ) : (
          <div className="bg-gray-30 dark:bg-gray-75 h-full w-full rounded-lg" />
        )}
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          variant="primary"
          data-dd-action-name={DATADOG_ACTIONS.ADD_MOTION}
          icon={<PlusIcon />}
          onClick={async () => {
            uiStore.setMotionFrontDoorMode('project');

            await projectStore.addExistingMotionToProject({
              existingJobID: browseItem.jobID,
              existingVariationNumber: 0,
              createAsNewProject: false,
              trimStart: browseAnimationPlayer.state.trimFrameStart,
              trimEnd: browseAnimationPlayer.state.trimFrameEnd,
            });
            onAddMotion();
          }}
        >
          {addText}
        </Button>
      </div>

      {reviewMode && (
        <div className="border-gray-30 dark:border-gray-60 relative mt-4 border-t">
          <div className="dark:bg-gray-90 w-full bg-white p-4">
            <BrowseRecordFootContacts
              browseItem={browseItem}
              onFeedbackSubmitted={handleFeedbackSubmitted}
            />
          </div>
        </div>
      )}
    </div>
  );
}
